import { useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Stepper,
  StepperIndicator,
  StepperItems,
  useStepperContext,
} from "~/components/ui/stepper";
import { ValidationNavigation } from "./ValidationNavigation";
import { FileUploadStep, FileValidationStep, SummaryStep } from "./ValidationSteps";
import { IngestionProvider, useIngestionContext } from "./context/IngestionContext";
import { useValidationAndProfiling } from "./hooks/useValidationAndProfiling";
import { FileValidationModalProps, FileValidationStepperState } from "./types";

/**
 * Component to sync validation/profiling state to stepper context.
 * This allows child step components to access validation state
 * via `useStepperContext<FileValidationStepperState>()`.
 *
 * File selection is handled by IngestionContext directly.
 * StepperStateSync only handles validation/profiling state.
 */
const StepperStateSync = ({
  validationState,
  dataProfile,
  profilingError,
  isValidating,
  isProfiling,
  startValidation,
  startProfiling,
  ingestion,
  isLoadingIngestion,
  recoveredSourceFile,
}: FileValidationStepperState) => {
  const { setStepperState } = useStepperContext<FileValidationStepperState>();
  const hasInitialized = useRef(false);

  // Sync state to stepper context, using ref to prevent render loops
  useEffect(() => {
    if (!hasInitialized.current && !ingestion && !isValidating && !isProfiling) {
      return;
    }
    hasInitialized.current = true;

    setStepperState?.((prev) => ({
      ...prev,
      validationState,
      dataProfile,
      profilingError,
      isValidating,
      isProfiling,
      startValidation,
      startProfiling,
      ingestion,
      isLoadingIngestion,
      recoveredSourceFile,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    validationState,
    dataProfile,
    profilingError,
    isValidating,
    isProfiling,
    ingestion,
    isLoadingIngestion,
    recoveredSourceFile,
  ]);

  return null;
};

/**
 * Inner modal content that has access to IngestionContext.
 */
const FileValidationModalContent = ({
  onClose,
  onFileValidated,
  onConfirm,
}: Omit<FileValidationModalProps, "open">) => {
  const { selectedFile, pendingLocalFile } = useIngestionContext();

  // Don't pass sourceFile if there's a pending upload - wait for upload to complete
  // This prevents validation from starting with the wrong (old) file
  const sourceFileUri = pendingLocalFile ? null : selectedFile?.s3_uri || selectedFile?.key;
  const {
    validationState,
    dataProfile,
    profilingError,
    isValidating,
    isProfiling,
    startValidation,
    startProfiling,
    ingestion,
    isLoadingIngestion,
    recoveredSourceFile,
  } = useValidationAndProfiling({ sourceFile: sourceFileUri });

  const stepLabels = ["Source File", "Validation", "Summary"];

  // Initial stepper state - validation/profiling only
  // File selection is handled by IngestionContext
  const stepperInitialValue: FileValidationStepperState = {
    validationState,
    dataProfile,
    profilingError,
    isValidating,
    isProfiling,
    startValidation,
    startProfiling,
    ingestion,
    isLoadingIngestion,
    recoveredSourceFile,
  };

  /**
   * Handle modal close (cancel). No reset needed — in-memory state is cleaned up
   * when the IngestionProvider unmounts, and URL params are intentionally preserved.
   */
  const handleClose = () => {
    onClose();
  };

  return (
    <DialogContent
      className="sm:max-w-3xl max-h-[95vh] flex flex-col"
      onEscapeKeyDown={(e) => e.preventDefault()}
      onPointerDownOutside={(e) => e.preventDefault()}
    >
      <DialogHeader className="shrink-0">
        <DialogTitle>Prepare File for Ingestion</DialogTitle>
        <DialogDescription>
          Ensure your file meets ingestion requirements before adding it to your table.
        </DialogDescription>
      </DialogHeader>

      <Stepper initialValue={stepperInitialValue} initialStep={0}>
        {/* Sync validation state to stepper context for child components */}
        <StepperStateSync
          validationState={validationState}
          dataProfile={dataProfile}
          profilingError={profilingError}
          isValidating={isValidating}
          isProfiling={isProfiling}
          startValidation={startValidation}
          startProfiling={startProfiling}
          ingestion={ingestion}
          isLoadingIngestion={isLoadingIngestion}
          recoveredSourceFile={recoveredSourceFile}
        />

        <div className="flex flex-col flex-1 min-h-0">
          <div className="shrink-0 mb-4">
            <StepperIndicator stepLabels={stepLabels} />
          </div>

          <div className="flex-1 overflow-y-auto">
            <StepperItems>
              <FileUploadStep />
              <FileValidationStep />
              <SummaryStep />
            </StepperItems>
          </div>
        </div>

        <ValidationNavigation
          onClose={handleClose}
          onFileValidated={onFileValidated}
          onConfirm={onConfirm}
        />
      </Stepper>
    </DialogContent>
  );
};

/**
 * FileValidationModal
 *
 * Modal for validating files before ingestion.
 * Wrapped with IngestionProvider to manage ingestion lifecycle.
 */
const FileValidationModal = ({
  open,
  onClose,
  onFileValidated,
  onConfirm,
}: FileValidationModalProps) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <IngestionProvider>
        <FileValidationModalContent
          onClose={onClose}
          onFileValidated={onFileValidated}
          onConfirm={onConfirm}
        />
      </IngestionProvider>
    </Dialog>
  );
};

export default FileValidationModal;
